import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import requestConfig from 'Models/uiConfig/requestConfig';
import type { ConfigMap, RuntimeConfigMap } from 'Models/uiConfig/requestTypes';
import type { ServerModel } from 'Modules/serverApi/types';
import { getCartUrls } from 'Platforms/shopify/platformHelpers';
import { isTruthy } from 'Utils/guards';
import t from 'Utils/translate';

type FitmentResponse = ServerModel.VerifyFitmentCallbackResponse;

type ConvermaxTemplateConfigMap = ConfigMap<RuntimeConfigMap>;

export default {
  getVerifyFitmentOrderNotes,
  onVehicleSelected,
  onVehicleDiscarded,
  onVerifyFitmentInitResponseReceived,
  onVerifyFitmentResponseReceived,
};

function onVehicleSelected(vehicle: Record<string, string>): void {
  setVehicleAtCart(vehicle);
}

function onVehicleDiscarded(): void {
  resetVehicleAtCart();
}

function onVerifyFitmentInitResponseReceived(response: FitmentResponse): void {
  setNotesOnCartItems(response);
  setVehicleInfoAtCart(response);
}

function onVerifyFitmentResponseReceived(response: FitmentResponse): void {
  setNotesOnCartItems(response);
  setVehicleInfoAtCart(response);
}

function getVerifyFitmentOrderNotes(fits: string, vehicleString: string, fieldsToSpecify = ''): string {
  const translations: Record<string, string> = {
    yes: t('verify_fitment_notes_fits', { vehicleString }),
    no: t('verify_fitment_notes_does_not_fit', { vehicleString }),
    aggressive: t('verify_fitment_notes_aggressive_fits', { vehicleString }),
    universal: t('verify_fitment_notes_universal'),
    unknown: t('verify_fitment_notes_unknown'),
    specify: t('verify_fitment_notes_specify', { fieldsToSpecify, vehicleString }),
  };

  return translations[fits] || '';
}

function setVehicleAtCart(vehicle: Record<string, string>): void {
  // `vehicle` originates from `loadSelectedVehicle` (browser storage),
  // and the static type `Record<string, string>` is a *promise*, not a
  // proof. A merchant who hand-edited localStorage (or an old build
  // that wrote numbers) could send `{Year: 2024}` here; the template
  // literal join below would silently coerce it to `'2024'`. Filter
  // each field through a string-only sieve so the cart attribute bag
  // matches the contract.
  const sanitized: Record<string, string> = {};
  Object.entries(vehicle).forEach(([key, value]) => {
    if (typeof value === 'string') {
      sanitized[key] = value;
    }
  });

  const vehicleString = fitmentSearchConfig.fields
    .map((field) => sanitized[field])
    .filter(isTruthy)
    .join(' ');

  const attributes = { ...sanitized, Vehicle: vehicleString };

  setCartAttributes(attributes);
}

function setVehicleInfoAtCart(response: FitmentResponse): void {
  const vehicleInfoUrl = getVehicleInfoUrl(response);

  if (vehicleInfoUrl) {
    setCartAttributes({ __vehicleInfoUrl: vehicleInfoUrl });
  }
}

function resetVehicleAtCart(): void {
  const emptyVehicle = fitmentSearchConfig.fields.reduce(
    (acc: Record<string, null>, cur) => ({ ...acc, [cur]: null }),
    {},
  );

  const attributes = { ...emptyVehicle, Vehicle: null, __vehicleInfoUrl: null };

  setCartAttributes(attributes);
}

function getVehicleInfoUrl(response: FitmentResponse): string | undefined {
  if (!response?.Extra?.WheelTireVehicleInfo) {
    return;
  }

  const vehicleInfoUrl = new URL(`https://${requestConfig.storeId}.myconvermax.com/ymm/vehicleinfo.html`);

  const vehicle: Record<string, string> = Object.fromEntries(
    response.Facets.flatMap((f) => {
      const selectedValue = f.Selection?.[0]?.Value;
      return fitmentSearchConfig.fields.includes(f.FieldName) && selectedValue
        ? [[f.FieldName, selectedValue]]
        : [];
    }),
  );

  if (fitmentSearchConfig.baseFields.every((f) => vehicle[f])) {
    Object.entries(vehicle).forEach(([field, value]) => {
      vehicleInfoUrl.searchParams.set(field, value);
    });

    return vehicleInfoUrl.toString();
  }
}

function setCartAttributes(attributes: Record<string, string | null>): void {
  fetch(`${getCartUrls().update}.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ attributes }),
  })
    .then(async (response) => {
      if (!response.ok) {
        const responseText = await response.text();
        console.error(parseJsonOrText(responseText));
      }
    })
    .catch((error) => {
      console.error(error);
    });
}

function parseJsonOrText(responseText: string): unknown {
  try {
    return JSON.parse(responseText) as unknown;
  } catch {
    return responseText;
  }
}

/**
 * Read a boolean flag out of the merchant-controlled
 * `window.Convermax.templates.config.fitmentSearchConfig` bag without
 * trusting its static type. The path traverses three layers of
 * potentially-undefined objects, each of which a merchant override
 * could replace with a non-object value.
 */
function getFitmentTemplateBool(field: string): boolean {
  const config = window.Convermax.templates?.config;
  if (!config || typeof config !== 'object') {
    return false;
  }
  const fitmentBag = (config as ConvermaxTemplateConfigMap).fitmentSearchConfig;
  if (!fitmentBag || typeof fitmentBag !== 'object') {
    return false;
  }
  return Boolean((fitmentBag as Record<string, unknown>)[field]);
}

function setNotesOnCartItems({ Facets, Items }: FitmentResponse): void {
  const forms = window.document.querySelectorAll(
    `form[action^="/cart/add"], form[action^="${getCartUrls().add}"]`,
  );

  if (!getFitmentTemplateBool('hideVerifyOnCartItems')) {
    const fits = Items[0]?.fitsTheVehicle;

    if (forms && fits) {
      const selectedFields = fitmentSearchConfig.fields.map(
        (field) => Facets.find((f) => f.FieldName === field)?.Selection?.[0]?.Value,
      );

      const vehicleString = selectedFields.filter(isTruthy).join(' ');

      if (vehicleString) {
        forms.forEach((form) => {
          const vehicleInputCandidate = form.querySelector('.cm_verify-message-property');
          const vehicleInput =
            vehicleInputCandidate instanceof HTMLInputElement ? vehicleInputCandidate : null;

          if (vehicleInput) {
            vehicleInput.value = getVerifyFitmentOrderNotes(fits, vehicleString);
          } else {
            const input = document.createElement('input');
            input.className = 'cm_verify-message-property';
            input.type = 'hidden';
            input.name = 'properties[Fitment]';
            input.value = getVerifyFitmentOrderNotes(
              fits,
              vehicleString,
              fitmentSearchConfig.fields
                .filter((field) => !Facets.find((f) => f.FieldName === field))
                .join(', '),
            );

            form.appendChild(input);
          }
        });
      }
    }
  }
}
